import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Menu,
    X,
    ChevronRight,
    Star,
    Play,
    Award,
    Users,
    PenTool,
    Instagram,
    Youtube,
    Facebook,
    Mail,
    MapPin,
    TrendingUp,
    Mic,
    Zap
} from 'lucide-react';

// --- DATA & CONTENT ---

const NAV_LINKS = [
    { id: 'home', label: 'Home' },
    { id: 'sobre', label: 'Sobre Nós' },
    { id: 'cursos', label: 'Cursos' },
    { id: 'palestras', label: 'Palestras' },
    { id: 'blog', label: 'Blog' },
    { id: 'videos', label: 'Vídeos' },
    { id: 'canais', label: 'Canais' },
];

const HERO_CONTENT = {
    // Headline ajustada para o texto fornecido
    titlePart1: "Onde a Paixão por Carros",
    titlePart2: "Vira um Negócio de Milhões",
    subtitle: "Acesse o maior hub de conhecimento gratuito para detailers. Domine da técnica à gestão com os maiores players do mercado (Maxpro, Nasiol, Nano Ceramic e mais).",
    cta: "Quero me Profissionalizar Gratuitamente"
};

// Dados atualizados conforme o texto "20+ palestrantes", "20h conteudo"
const STATS = [
    { number: "20+", label: "Palestrantes Renomados" },
    { number: "20h+", label: "Conteúdo Prático" },
    { number: "Hub", label: "100% Online e Gratuito" }, // Destaque para o Gratuito
];

// Pilares atualizados conforme o TXT
const FEATURES = [{
        title: "Mestria Técnica",
        desc: "Do Ciclo de Sinner à revolução do grafeno. Domine pH de produtos e polimento técnico com quem fabrica a tecnologia.",
        icon: < Award className = "w-6 h-6" / >
    },
    {
        title: "Gestão de Elite",
        desc: "Saia da 'conta de padeiro'. Aprenda a calcular hora trabalhada, separar PJ de PF e precificar para ter lucro real.",
        icon: < TrendingUp className = "w-6 h-6" / >
    },
    {
        title: "Vendas e Oratória",
        desc: "Transforme comunicação em uma habilidade de milhões. Use PNL e gatilhos mentais para vender projetos, não apenas lavagens.",
        icon: < Mic className = "w-6 h-6" / >
    }
];

const SPEAKERS = [
    { name: "Júnior Vieira", role: "Estúdio Padrão Americano", company: "Nasiol" },
    { name: "Leandro Terab", role: "Mestre da Precificação", company: "Maxpro" },
    { name: "Karina Mosa", role: "Posicionamento Premium", company: "Monkeys Garagem" },
    { name: "Ítalo Freire", role: "Marketing e Vendas", company: "Solar" },
    { name: "Alessandro", role: "Nanotecnologia", company: "Nano Ceramic Protect" }
];

// --- COMPONENTS ---

const Button = ({ children, variant = 'primary', onClick, className = '' }) => {
    const baseStyle = "px-6 py-3 rounded-full font-medium tracking-wide transition-all duration-300 transform active:scale-95 flex items-center gap-2 relative overflow-hidden group text-sm md:text-base";

    const variants = {
        primary: "bg-yellow-400 text-black hover:bg-yellow-300 font-bold shadow-[0_0_20px_rgba(250,204,21,0.4)]",
        secondary: "bg-transparent border border-gray-700 text-white hover:border-yellow-400 hover:text-yellow-400",
        outline: "bg-transparent border border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black"
    };

    return ( <
        button onClick = { onClick }
        className = { `${baseStyle} ${variants[variant]} ${className}` } >
        <
        span className = "relative z-10 flex items-center gap-2" > { children } < /span> {
            variant === 'primary' && ( <
                div className = "absolute inset-0 h-full w-full bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full group-hover:animate-shimmer" / >
            )
        } <
        /button>
    );
};

const SectionHeader = ({ title, subtitle, align = 'center' }) => ( <
    div className = { `mb-16 ${align === 'center' ? 'text-center' : 'text-left'}` } >
    <
    motion.h2 initial = {
        { opacity: 0, y: 20 } }
    whileInView = {
        { opacity: 1, y: 0 } }
    viewport = {
        { once: true } }
    className = "text-3xl md:text-5xl font-bold tracking-tighter mb-4 text-white uppercase" >
    { title } <
    /motion.h2> {
        subtitle && ( <
            motion.p initial = {
                { opacity: 0, y: 20 } }
            whileInView = {
                { opacity: 1, y: 0 } }
            viewport = {
                { once: true } }
            transition = {
                { delay: 0.1 } }
            className = "text-gray-400 text-lg max-w-2xl mx-auto font-light leading-relaxed" >
            { subtitle } <
            /motion.p>
        )
    } <
    /div>
);

// --- PAGES ---

const HomePage = ({ setPage }) => ( <
    > { /* HERO SECTION */ } <
    section className = "relative min-h-screen flex items-center justify-center overflow-hidden py-20" >
    <
    div className = "absolute inset-0 bg-black z-0" >
    <
    div className = "absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,_rgba(20,20,20,1)_0%,_rgba(0,0,0,1)_100%)]" / >
    <
    div className = "absolute top-1/4 left-1/4 w-96 h-96 bg-yellow-600/10 rounded-full blur-[100px] animate-pulse" / >
    <
    div className = "absolute bottom-1/4 right-1/4 w-96 h-96 bg-gray-700/10 rounded-full blur-[100px] animate-pulse"
    style = {
        { animationDelay: '2s' } }
    /> <
    div className = "absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20" / >
    <
    /div>

    <
    div className = "container mx-auto px-6 relative z-10 text-center flex flex-col items-center" >
    <
    motion.div initial = {
        { opacity: 0, scale: 0.9 } }
    animate = {
        { opacity: 1, scale: 1 } }
    transition = {
        { duration: 0.8, ease: "easeOut" } }
    className = "mb-8 inline-block" >
    <
    span className = "px-4 py-2 rounded-sm border-l-4 border-yellow-500 bg-white/5 backdrop-blur-sm text-xs md:text-sm uppercase tracking-[0.2em] text-white font-bold" >
    Na Velocidade do Conhecimento <
        /span> <
        /motion.div>

    <
    motion.h1 initial = {
        { opacity: 0, y: 30 } }
    animate = {
        { opacity: 1, y: 0 } }
    transition = {
        { duration: 0.8, delay: 0.2 } }
    className = "text-4xl md:text-7xl lg:text-8xl font-bold tracking-tighter text-white mb-8 leading-[1.1]" >
    { HERO_CONTENT.titlePart1 } < br className = "hidden md:block" / >
    <
    span className = "text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-600" > { HERO_CONTENT.titlePart2 } <
    /span> <
    /motion.h1>

    <
    motion.p initial = {
        { opacity: 0, y: 30 } }
    animate = {
        { opacity: 1, y: 0 } }
    transition = {
        { duration: 0.8, delay: 0.4 } }
    className = "text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-10 font-light leading-relaxed" >
    { HERO_CONTENT.subtitle } <
    /motion.p>

    <
    motion.div initial = {
        { opacity: 0, y: 30 } }
    animate = {
        { opacity: 1, y: 0 } }
    transition = {
        { duration: 0.8, delay: 0.6 } }
    className = "flex flex-col md:flex-row gap-4 justify-center w-full md:w-auto" >
    <
    Button onClick = {
        () => setPage('cursos') }
    className = "justify-center" > { HERO_CONTENT.cta } < ChevronRight className = "w-4 h-4" / >
    <
    /Button> <
    Button variant = "secondary"
    onClick = {
        () => setPage('videos') }
    className = "justify-center" >
    Ver Trailer < Play className = "w-4 h-4 ml-1" / >
    <
    /Button> <
    /motion.div> <
    /div>

    { /* Scroll Indicator */ } <
    motion.div initial = {
        { opacity: 0 } }
    animate = {
        { opacity: 1 } }
    transition = {
        { delay: 1.5, duration: 1 } }
    className = "absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2" >
    <
    span className = "text-[10px] uppercase tracking-[0.2em] text-gray-500" > Scroll < /span> <
    div className = "w-[1px] h-12 bg-gradient-to-b from-yellow-500/50 to-transparent" / >
    <
    /motion.div> <
    /section>

    { /* MANIFESTO SECTION (Propósito) */ } <
    section className = "py-24 bg-zinc-950 relative overflow-hidden" >
    <
    div className = "absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-yellow-900/5 to-transparent pointer-events-none" / >
    <
    div className = "container mx-auto px-6" >
    <
    div className = "flex flex-col md:flex-row gap-16 items-center" >
    <
    div className = "w-full md:w-1/2" >
    <
    div className = "inline-block mb-4" >
    <
    span className = "text-yellow-500 font-bold tracking-widest uppercase text-sm flex items-center gap-2" >
    <
    span className = "w-8 h-[2px] bg-yellow-500" > < /span>
    O Movimento <
    /span> <
    /div> <
    h2 className = "text-3xl md:text-5xl font-bold text-white mb-6 leading-tight" >
    Não é apenas um evento. < br / >
    É o fim do <span className = "text-gray-500 line-through decoration-yellow-500" > amadorismo < /span>. <
        /h2> <
        p className = "text-gray-400 text-lg leading-relaxed mb-6" >
        A Jornada Detail Mais é um movimento de democratização do ensino de alto nível.Unimos mais de < strong className = "text-white" > 20 palestrantes renomados < /strong> para entregar <strong className="text-white">20 horas de conteúdo prático</strong > . <
            /p> <
            p className = "text-gray-400 text-lg leading-relaxed" >
            Nosso objetivo é encurtar sua distância até o sucesso, tirando você do "amadorismo genérico"
            e
            colocando - o no topo do mercado premium. <
                /p> <
                /div> <
                div className = "w-full md:w-1/2 grid grid-cols-2 gap-4" > {
                    STATS.map((stat, index) => ( <
                        div key = { index }
                        className = "bg-white/5 border border-white/10 p-6 rounded-lg text-center backdrop-blur-sm hover:border-yellow-500/50 transition-colors" >
                        <
                        div className = "text-4xl md:text-5xl font-bold text-white mb-2" > { stat.number } < /div> <
                        div className = "text-xs uppercase tracking-widest text-yellow-500" > { stat.label } < /div> <
                        /div>
                    ))
                } <
                /div> <
                /div> <
                /div> <
                /section>

            { /* PILLARS OF KNOWLEDGE */ } <
            section className = "py-32 bg-black relative" >
            <
            div className = "container mx-auto px-6" >
            <
            SectionHeader
        title = "Os Pilares do Conhecimento"
    subtitle = "Nossa grade curricular foi desenhada para cobrir 360º do seu negócio. Não adianta saber polir se você não sabe vender." /
    >

    <
    div className = "grid grid-cols-1 md:grid-cols-3 gap-8" > {
        FEATURES.map((feature, index) => ( <
            motion.div key = { index }
            initial = {
                { opacity: 0, y: 20 } }
            whileInView = {
                { opacity: 1, y: 0 } }
            viewport = {
                { once: true } }
            transition = {
                { delay: index * 0.2 } }
            className = "group p-10 border border-white/10 rounded-none bg-zinc-900/30 hover:bg-zinc-900/80 transition-all duration-500 relative overflow-hidden" >
            <
            div className = "absolute top-0 left-0 w-1 h-0 bg-yellow-500 group-hover:h-full transition-all duration-500" / >
            <
            div className = "w-14 h-14 rounded-sm bg-yellow-500/10 flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-500 text-yellow-500" > { feature.icon } <
            /div> <
            h3 className = "text-2xl font-bold text-white mb-4" > { feature.title } < /h3> <
            p className = "text-gray-400 font-light leading-relaxed" > { feature.desc } < /p> <
            /motion.div>
        ))
    } <
    /div> <
    /div> <
    /section>

    { /* DREAM TEAM SECTION */ } <
    section className = "py-32 bg-zinc-950 border-t border-white/5" >
    <
    div className = "container mx-auto px-6" >
    <
    SectionHeader title = 'O "Dream Team" do Detail'
    subtitle = "Aprenda diretamente com as maiores autoridades do mercado brasileiro. Acesso exclusivo a quem vive o campo de batalha." /
    >

    <
    div className = "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" > {
        SPEAKERS.map((speaker, index) => ( <
            motion.div key = { index }
            initial = {
                { opacity: 0, scale: 0.95 } }
            whileInView = {
                { opacity: 1, scale: 1 } }
            viewport = {
                { once: true } }
            transition = {
                { delay: index * 0.1 } }
            className = "flex items-center gap-4 p-4 border border-white/5 bg-white/[0.02] hover:bg-white/[0.05] hover:border-yellow-500/30 transition-all cursor-default" >
            <
            div className = "w-16 h-16 bg-gradient-to-br from-gray-800 to-black rounded-full border border-white/10 flex items-center justify-center text-gray-600 font-bold text-xl" > { speaker.name.charAt(0) } <
            /div> <
            div >
            <
            h4 className = "text-white font-bold text-lg" > { speaker.name } < /h4> <
            p className = "text-yellow-500 text-xs uppercase tracking-wider font-bold mb-1" > { speaker.company } < /p> <
            p className = "text-gray-500 text-sm leading-tight" > { speaker.role } < /p> <
            /div> <
            /motion.div>
        ))
    }

    { /* Card de Chamada para todos */ } <
    div className = "flex flex-col items-center justify-center p-6 border border-dashed border-white/20 bg-transparent text-center" >
    <
    p className = "text-gray-400 mb-2" > e muito mais... < /p> <
    button onClick = {
        () => setPage('palestras') }
    className = "text-yellow-500 font-bold underline hover:text-white transition-colors" >
    Ver Lista Completa <
    /button> <
    /div> <
    /div> <
    /div> <
    /section>

    { /* CTA FINAL */ } <
    section className = "py-24 bg-yellow-500 text-black relative overflow-hidden" >
    <
    div className = "absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10" / >
    <
    div className = "container mx-auto px-6 text-center relative z-10" >
    <
    h2 className = "text-4xl md:text-6xl font-bold tracking-tighter mb-6" > Comece Sua Jornada Agora < /h2> <
    p className = "text-xl md:text-2xl font-medium mb-10 max-w-3xl mx-auto opacity-80" >
    Junte - se a milhares de profissionais que estão redefinindo o padrão da estética automotiva. <
    /p> <
    button onClick = {
        () => setPage('cursos') }
    className = "bg-black text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-zinc-800 hover:scale-105 transition-all shadow-xl" >
    Acessar Hub Gratuito <
    /button> <
    /div> <
    /section> <
    />
);

// --- PLACEHOLDER PAGES (To demonstrate routing) ---

const GenericPage = ({ title, icon }) => ( <
    div className = "min-h-screen bg-black pt-32 pb-20 px-6 flex flex-col items-center" > { /* Ícone amarelo */ } <
    div className = "text-yellow-500 mb-6" > { icon } < /div> <
    h1 className = "text-4xl md:text-6xl font-bold text-white mb-6 tracking-tighter text-center" > { title } < /h1> <
    p className = "text-gray-400 max-w-xl text-center font-light mb-12" >
    Esta página está em construção com o padrão de qualidade Jornada Detail Mais.Conteúdo exclusivo em breve. <
    /p> <
    div className = "w-full max-w-4xl h-64 border border-white/10 rounded-2xl bg-white/[0.02] flex items-center justify-center relative overflow-hidden" >
    <
    div className = "absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent opacity-50" > < /div> <
    span className = "text-gray-600 text-sm tracking-widest uppercase" > [Área de Conteúdo Dinâmico] < /span> <
    /div> <
    /div>
);

// --- MAIN APP COMPONENT ---

export default function App() {
    const [activePage, setActivePage] = useState('home');
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    // Handle scroll effect for Navbar
    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Scroll to top on page change
    useEffect(() => {
        window.scrollTo(0, 0);
        setIsMenuOpen(false);
    }, [activePage]);

    const renderContent = () => {
            switch (activePage) {
                case 'home':
                    return <HomePage setPage = { setActivePage }
                    />;
                case 'sobre':
                    return <GenericPage title = "Sobre Nós"
                    icon = { < Users size = { 48 }
                            />} / > ;
                            case 'cursos':
                                    return <GenericPage title = "Cursos Profissionais"
                                icon = { < Award size = { 48 }
                                    />} / > ;
                                    case 'palestras':
                                            return <GenericPage title = "Portfólio de Palestras"
                                        icon = { < PenTool size = { 48 }
                                            />} / > ;
                                            case 'blog':
                                                    return <GenericPage title = "Blog Detail"
                                                icon = { < PenTool size = { 48 }
                                                    />} / > ;
                                                    case 'videos':
                                                            return <GenericPage title = "Vídeos Exclusivos"
                                                        icon = { < Play size = { 48 }
                                                            />} / > ;
                                                            case 'canais':
                                                                    return <GenericPage title = "Canais de Atendimento"
                                                                icon = { < MapPin size = { 48 }
                                                                    />} / > ;
                                                                    default: return <HomePage setPage = { setActivePage }
                                                                    />;
                                                                }
                                                        };

                                                        return (
                                                            // Selection color alterado para amarelo escuro
                                                            <
                                                            div className = "bg-black min-h-screen text-white font-sans selection:bg-yellow-900 selection:text-white" >
                                                            <
                                                            style > { `
        @keyframes shimmer {
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      ` } < /style>

                                                            { /* NAVIGATION */ } <
                                                            nav className = { `fixed top-0 left-0 right-0 z-50 transition-all duration-500 border-b ${
          scrolled ? 'bg-black/90 backdrop-blur-md border-white/10 py-4' : 'bg-transparent border-transparent py-6'
        }` } >
                                                            <
                                                            div className = "container mx-auto px-6 flex justify-between items-center" > { /* Logo Area - Atualizado para bater com a imagem enviada */ } <
                                                            div className = "text-xl md:text-2xl font-bold tracking-tighter cursor-pointer flex items-center gap-3"
                                                            onClick = {
                                                                () => setActivePage('home') } >
                                                            { /* Logo box estilizado como na imagem de perfil (Quadrado preto, borda branca, texto dentro) */ } <
                                                            div className = "w-10 h-10 border border-white bg-black flex flex-col items-center justify-center p-1 leading-none" >
                                                            <
                                                            span className = "text-[6px] text-white" > JORNADA < /span> <
                                                            span className = "text-[8px] text-yellow-400 font-bold" > DETAIL < /span> <
                                                            span className = "text-[6px] text-white" > MAIS < /span> <
                                                            /div>

                                                            <
                                                            div className = "flex flex-col md:flex-row md:gap-1 leading-none" >
                                                            <
                                                            span className = "text-white" > JORNADA < /span> <
                                                            span className = "text-yellow-400 font-bold" > DETAIL < /span> <
                                                            span className = "text-white font-light" > MAIS < /span> <
                                                            /div> <
                                                            /div>

                                                            { /* Desktop Menu - Adjusted to show ALL links */ } <
                                                            div className = "hidden lg:flex items-center gap-6 xl:gap-8" > {
                                                                NAV_LINKS.map((link) => ( <
                                                                    button key = { link.id }
                                                                    onClick = {
                                                                        () => setActivePage(link.id) }
                                                                    className = { `text-xs xl:text-sm tracking-widest uppercase transition-colors hover:text-yellow-400 ${
                  activePage === link.id ? 'text-white font-medium border-b border-yellow-500 pb-1' : 'text-gray-400'
                }` } >
                                                                    { link.label } <
                                                                    /button>
                                                                ))
                                                            } <
                                                            Button variant = "primary"
                                                            className = "py-2 px-6 text-sm"
                                                            onClick = {
                                                                () => setActivePage('cursos') } >
                                                            Matricule - se <
                                                            /Button> <
                                                            /div>

                                                            { /* Mobile Toggle */ } <
                                                            button className = "lg:hidden text-white hover:text-yellow-400 transition-colors"
                                                            onClick = {
                                                                () => setIsMenuOpen(!isMenuOpen) } > { isMenuOpen ? < X / > : < Menu / > } <
                                                            /button> <
                                                            /div> <
                                                            /nav>

                                                            { /* MOBILE MENU OVERLAY */ } <
                                                            AnimatePresence > {
                                                                isMenuOpen && ( <
                                                                    motion.div initial = {
                                                                        { opacity: 0, y: -20 } }
                                                                    animate = {
                                                                        { opacity: 1, y: 0 } }
                                                                    exit = {
                                                                        { opacity: 0, y: -20 } }
                                                                    className = "fixed inset-0 z-40 bg-zinc-950 pt-24 px-6 lg:hidden" >
                                                                    <
                                                                    div className = "flex flex-col gap-6" > {
                                                                        NAV_LINKS.map((link) => ( <
                                                                            button key = { link.id }
                                                                            onClick = {
                                                                                () => setActivePage(link.id) }
                                                                            // Borda lateral amarela
                                                                            className = { `text-2xl text-left font-light border-b border-white/10 pb-4 ${
                    activePage === link.id ? 'text-white pl-4 border-l-2 border-l-yellow-500' : 'text-gray-500'
                  }` } >
                                                                            { link.label } <
                                                                            /button>
                                                                        ))
                                                                    } <
                                                                    /div> <
                                                                    /motion.div>
                                                                )
                                                            } <
                                                            /AnimatePresence>

                                                            { /* DYNAMIC CONTENT */ } <
                                                            main className = "min-h-screen" > { renderContent() } <
                                                            /main>

                                                            { /* FOOTER */ } <
                                                            footer className = "bg-black py-16 border-t border-white/10" >
                                                            <
                                                            div className = "container mx-auto px-6" >
                                                            <
                                                            div className = "grid grid-cols-1 md:grid-cols-4 gap-12 mb-16" >
                                                            <
                                                            div className = "col-span-1 md:col-span-2" >
                                                            <
                                                            div className = "text-2xl font-bold tracking-tighter mb-6 flex gap-1" >
                                                            <
                                                            span > JORNADA < /span> <
                                                            span className = "text-yellow-400" > DETAIL < /span> <
                                                            span className = "font-light" > MAIS < /span> <
                                                            /div> <
                                                            p className = "text-gray-500 max-w-sm font-light leading-relaxed" >
                                                            Elevando o padrão da estética automotiva no Brasil.Técnica, paixão e negócios em um só lugar. <
                                                            /p> <
                                                            /div>

                                                            <
                                                            div >
                                                            <
                                                            h4 className = "text-white font-bold mb-6 border-l-2 border-yellow-500 pl-3" > Explorar < /h4> <
                                                            ul className = "space-y-4" > {
                                                                NAV_LINKS.map(item => ( <
                                                                    li key = { item.id } >
                                                                    <
                                                                    button onClick = {
                                                                        () => setActivePage(item.id) }
                                                                    className = "text-gray-500 hover:text-yellow-400 transition-colors text-sm" >
                                                                    { item.label } <
                                                                    /button> <
                                                                    /li>
                                                                ))
                                                            } <
                                                            /ul> <
                                                            /div>

                                                            <
                                                            div >
                                                            <
                                                            h4 className = "text-white font-bold mb-6 border-l-2 border-yellow-500 pl-3" > Contato < /h4> <
                                                            ul className = "space-y-4 text-sm text-gray-500" >
                                                            <
                                                            li className = "flex items-center gap-3" > < Mail size = { 16 }
                                                            className = "text-yellow-500" / > contato @jornadadetail.com < /li> <
                                                            li className = "flex items-center gap-3" > < MapPin size = { 16 }
                                                            className = "text-yellow-500" / > Santa Catarina, SC < /li> <
                                                            /ul> <
                                                            div className = "flex gap-4 mt-6" > {
                                                                [Instagram, Facebook, Youtube].map((Icon, i) => ( <
                                                                    a key = { i }
                                                                    href = "#"
                                                                    className = "w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-yellow-400 hover:text-black transition-all" >
                                                                    <
                                                                    Icon size = { 18 }
                                                                    /> <
                                                                    /a>
                                                                ))
                                                            } <
                                                            /div> <
                                                            /div> <
                                                            /div>

                                                            <
                                                            div className = "pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center text-xs text-gray-600" >
                                                            <
                                                            p > & copy; 2025 Jornada Detail Mais.Todos os direitos reservados. < /p> <
                                                            p className = "mt-2 md:mt-0" > Desenvolvido com excelência. < /p> <
                                                            /div> <
                                                            /div> <
                                                            /footer> <
                                                            /div>
                                                        );
                                                }